/**
 * Axway react internationalization module
 * @module axway-react-intl
 * @typicalname axway-react-intl
 */

const MAXIMUM_KEY_LENGTH = 40;

const React = require('react');
const ReactIntl = require('react-intl');

function stripFormat(str) {
	return str.replace(/\{\w+\}/g, '');
}

function isFormat(str) {
	return /\{\w+\}/.test(str);
}

function isUpperCase(str) {
	let lstr = str;
	if (isFormat(lstr)) {
		lstr = stripFormat(lstr);
		if (!lstr) {
			return false;
		}
	}
	return lstr === lstr.toUpperCase();
}

function isLowerCase(str) {
	let lstr = str;
	if (isFormat(lstr)) {
		lstr = stripFormat(lstr);
		if (!lstr) {
			return false;
		}
	}
	return lstr === lstr.toLowerCase();
}

function startsWithCapital(str) {
	return str[0] === str[0].toUpperCase();
}

function isCapitalized(str) {
	const capd = startsWithCapital(str);
	if (!capd) {
		return false;
	}
	if (str.length > 1) {
		// true if the rest of the string is lower-case
		return isLowerCase(str.slice(1));
	}
	return true;
}

function isCamelCase(str) {
	const parts = str.split(/\s+/);
	let cc = false;
	let i = 0;
	let part;

	if (parts.length < 2) {
		return false;
	}
	for (i = 0; i < parts.length; i += 1) {
		part = parts[i];
		if (isFormat(part)) {
			// ignore
			continue;
		}
		cc = isCapitalized(part);
		if (!cc) {
			return false;
		}
	}
	return cc;
}

/**
 * @summary Generates a human-readable keys from a string string.
 *
 * @description
 * The `text` is reduced to ascii characters only and generates a lower-case
 * key from the text.  The key is prefixed to maintain the properties of
 * the original text to maintain uniqueness.  E.g. to distinguish between
 * "text" and "TEXT", the key will be "lc_text" and "uc_text".  The
 * default (non-prefixed) key is capitalized text.  E.g. "Text" will have
 * the key "text".
 *
 * If `option` is specified as a string, then it is the key to use.  If
 * `option` is a number then it is the maximum length of the key.
 *
 * | PREFIX  | KEY                | TEXT               | DESCRIPTION |
 * | ------- | ------------------ |------------------- | ------------------- |
 * |         |example_name        |Example name        |Capitalized sentence|
 * |lc_      |lc_example_name     |example name        |Lower-case text|
 * |cc_      |cc_example_name     |Example Name        |Camel Case text|
 * |uc_      |uc_example_name     |EXAMPLE NAME        |Upper-case text|
 * |fmt_     |fmt_example_name    |Example name {fmt}  |Text contains format argument(s)|
 *
 * @public
 * @param {string} text - The text to use to generate a key.
 * @param {number|object} [option=] - A maximum length of key, or the actual key.
 * @return {string} The generated key.
 */
function generateKey(text, option) {
	let key;
	let lc;
	let uc;
	let cc;
	let fmt;
	let max;
	let parts;
	let part;
	let i;

	if (!text) {
		throw new TypeError('illegal argument: text');
	}
	// make an ascii string from the text to use as the key
	if (typeof (option) === 'string') {
		// explicit key
		key = option;
	} else {
		key = text.toLowerCase()
			.replace(/[^a-zA-Z]/g, '_') // convert non-ascii to '_'
			.replace(/(_{2,})/g, '_') // convert multiple '__' to '_'
			.replace(/(^_+|_+$)/g, ''); // strip leading or trailing '_'
		lc = isLowerCase(text);
		uc = isUpperCase(text);
		cc = isCamelCase(text);
		fmt = isFormat(text);
		if (lc) {
			key = `lc_${key}`;
		} else if (uc) {
			key = `uc_${key}`;
		} else if (cc) {
			key = `cc_${key}`;
		}
		if (fmt) {
			key = `fmt_${key}`;
		}
		if (!lc && !uc && !cc && !fmt && !startsWithCapital(text)) {
			// should be a capitalized string
			throw new Error('text must start with a capital, be lower-case, camel-case, or upper-case');
		}

		max = MAXIMUM_KEY_LENGTH;
		if (typeof (option) === 'number' && option > 0) {
			max = option;
		}

		if (key.length >= max) {
			parts = key.split('_');
			key = '';
			for (i = 0; i < parts.length; i += 1) {
				part = parts[i];
				if (key.length + part.length > max) {
					break;
				}
				if (i > 0) {
					key += '_';
				}
				key += part;
			}
		}
	}
	return key;
}

/**
 * Gets arguments for `formatMessage` and `FormattedMessage`,
 *
 * Depending on the types of `arg1`, `arg2`, and `arg3`.  If `arg1` is an
 * object, then it must have an `id` (an argument that is using a
 * predefined key) and `arg2` is optional `formatOptions`.
 *
 * If `arg1` is a string and `arg2` is undefined or an object, then a
 * unique key is generated from `arg1` (the text) and `arg2` is optional
 * `formatOptions`.
 *
 * If `arg1` is a string and `arg2` is also a string (or number), then a
 * unique key is generated from `arg1` (`arg2` is either a predefined key
 * or a max-length), and `arg3` is optional `formatOptions`.
 *
 * @param {string|object} arg1 - Text or an object containing a predefined `id`.
 * @param {string|number|object} arg2 - A predefined key, a max-length, or
 *		format options.
 * @param {object} arg3 - Format options if `arg2` provided.
 * @return {object} An object of `{ key, text, formatOptions }`
 *
 */
function getArguments(arg1, arg2, arg3) {
	if (typeof arg1 === 'object') {
		// arg1 is an object using a predefined key. `arg2` might be a format
		// option.
		return {
			key: arg1.id,
			formatOptions: arg2
		};
	} else {
		// arg1 is a string
		let formatOptions = arg3;
		if (typeof arg2 !== 'number' && typeof arg2 !== 'string') {
			// `arg2` is not used for generating the key, i.e. it is an object
			// and is a format option.
			formatOptions = arg2;
			arg2 = undefined;
		}
		return {
			key: generateKey(arg1, arg2),
			text: arg1,
			formatOptions
		};
	}
}

/**
 * @summary Defines a string format for internationalization.
 *
 * @description
 * Formats the text for internationalization.  If is not supplied,
 * the key is automatically generated from the text.  If arg is
 * supplied, and it is a string, then it is the key (no key is
 * generated).  If arg is an object, then it is used for formatting
 * in [react-intl](https://github.com/yahoo/react-intl/wiki/Components#string-formatting-components).
 *
 * @example
 * // Define an international string
 * this.intl.formatMsg('My interntional string');
 *
 * // Define an international string with predefined key
 * this.intl.formatMsg('My interntional string', 'my_key');
 *
 * // Define an international string with formatting
 * this.intl.formatMsg('My interntional {value}', {value:'string'});
 *
 * // Define an international string with HTML value
 * this.intl.formatHTMLMsg('My interntional {value}', {value: <b>value</b>});
 *
 * // Define an international string with HTML value with predefined key
 * this.intl.formatHTMLMsg('My interntional {value}', 'my_key', {value: <b>value</b>});
 *
 * // Provide HTML value with a predefined key (text defined separately)
 * this.intl.formatHTMLMsg({ id: 'my_key' }, {value: <b>value</b>});
 *
 * // Provide format value with a predefined key (text defined separately)
 * this.intl.formatMsg({ id: 'my_key' }, {value: 'value'});
 *
 * @param {string} text - The internationalized string.
 * @return {string} The formatted string.
 */
function intlFormatMessage(intl, text, key, options) {
	if (!intl) {
		throw new TypeError('component is not initialized or does not use injectIntl');
	}
	const args = getArguments(text, key, options);
	return intl.formatMessage(
		{ id: args.key, defaultMessage: args.text }, args.formatOptions);
}

function intlFormatHTMLMessage(intl, text, key, options) {
	if (!intl) {
		throw new TypeError('component is not initialized or does not use injectIntl');
	}
	const args = getArguments(text, key, options);
	return React.createElement(ReactIntl.FormattedMessage,
		{ id: args.key, defaultMessage: args.text, values: args.formatOptions });
}

/**
 * @summary Initializes a ReactJS component to work with internationalization.
 *
 * @example
 * import React from 'react';
 * import { IntlProvider } from 'react-intl';
 * import { injectIntl } from 'react-intl';
 *
 * class Exclaim {
 *     constructor(props) {
 *         super(props);
 *         IntlComponent.init(this);
 *     }
 *     render() {
 *         return <bold>{this.intl.formatMsg('omg!')}</bold>;
 *     }
 * }
 * export default injectIntl(Exclaim);
 *
 * @public
 * @param {Object} component - The ReactJS component to initialize.
 */
function init(component) {
	const intl = (component && component.props && component.props.intl) || null;
	component.intl = {
		formatMsg: intlFormatMessage.bind(null, intl),
		formatHTMLMsg: intlFormatHTMLMessage.bind(null, intl)
	};
}

module.exports = {
	init,
	generateKey
};
